import axios from 'axios';

import camelcaseKeys from 'camelcase-keys';
import snakeCaseKeys from 'snakecase-keys';

import toast from '@/plugins/sweetalert2.js';

import { routerStore } from '@/stores/route.js';
import { userStore } from '@/stores/user.js';
import errorCode from '@/i18n/zh-TW/errorCode.json';
import i18n from '@/plugins/i18n.js';
import { v4 as uuidv4 } from 'uuid';
const service = axios.create({
  baseURL: import.meta.env.VITE_API_SERVER_URL,
  timeout: 25000,
  withCredentials: false
});
let requestUUIDs = [];
service.interceptors.request.use(
  (config) => {
    if (config.loading) {
      userStore().loadingStart();
      config._uuid = uuidv4();
      requestUUIDs.push(config._uuid);
    }

    const { currentRoute } = routerStore();
    const token = localStorage.getItem('_token') || '';

    config.headers = {
      'Target-From': currentRoute?.value?.name,
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    };

    if (
      config.params &&
      typeof config.params === 'object' &&
      config.notSnakeCase !== true
    ) {
      config.params = snakeCaseKeys(config.params, { deep: true });
    }

    if (
      config.data &&
      typeof config.data === 'object' &&
      !(config.data instanceof FormData)
    ) {
      config.data = snakeCaseKeys(config.data, { deep: true });
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      // transform response data keys to camelcase
      response.data = camelcaseKeys(response.data, { deep: true });
    }

    // 驗證失敗
    if (response.data.msg === '10004') {
      routerStore().router?.push('/login');
    }
    if (response.data.msg) {
      toast.fire({
        toast: true,
        icon: 'error',
        iconColor: 'red',
        title: i18n.global.t('error_msg'),
        text:
          errorCode[response.data.msg] ||
          `${i18n.global.t('undefined_error_code')}：${response.data.msg}`
      });
    }

    if (response.config.loading) {
      const idx = requestUUIDs.indexOf(response.config._uuid);
      if (idx > -1) {
        requestUUIDs.splice(idx, 1);
      }
    }

    if (requestUUIDs.length === 0) {
      userStore().loadingEnd();
    }
    return response.data;
  },
  (error) => {
    if (error && error.response) {
      switch (error.response.status) {
        case 401: {
          routerStore().router?.push('/login');
          toast.fire({
            toast: true,
            icon: 'error',
            iconColor: 'red',
            title: i18n.global.t('error_msg'),
            text: errorCode[error.response.data.msg] || `${error.response.data.msg}`
          });
          userStore().loadingEnd();
          return Promise.reject(error);
        }
        case 400:
        case 403:
        case 404:
        case 405:
        case 408:
        case 500:
        case 501:
        case 502:
        case 503:
        case 504:
        case 505:
          error.message = i18n.global.t(`error_code.${error.response.status}`);
          break;
        default:
          error.message = `${i18n.global.t('error_link')} ${error.response.status}`;
      }
    } else {
      // 超時處理
      if (JSON.stringify(error).includes('timeout')) {
        console.log(
          `%c Error %c ${i18n.global.t('the_server_resp_timed_out_plz_refresh')} %c`,
          'background: #db3129 ; padding: 3px; border-radius: 3px 0 0 3px;  color: #fff',
          'background: #ff625a ; padding: 3px; border-radius: 0 3px 3px 0;  color: #111',
          'background:transparent'
        );
        toast.fire({
          toast: true,
          icon: 'error',
          iconColor: 'red',
          title: i18n.global.t('error_msg'),
          text: `${i18n.global.t('the_server_resp_timed_out_plz_refresh')}`
        });
      }
    }

    userStore().setErrorMsg(error.response);
    userStore().loadingEnd();
    requestUUIDs = [];

    console.log(
      `%c ${i18n.global.t('error_code.400')} %c ${error.message} %c`,
      'background: #db3129 ; padding: 3px; border-radius: 3px 0 0 3px;  color: #fff',
      'background: #ff625a ; padding: 3px; border-radius: 0 3px 3px 0;  color: #111',
      'background:transparent'
    );

    routerStore().router?.push('/error');
    return Promise.reject(error);
  }
);

export default service;
